
export const MIN_ROLES_FOR_ORG_DESIGN = 5;

// POST /create-org-redesign-version (doc §5) rejects with this specific
// error_code when a PUBLISHED version already exists for the PAIC project —
// an expected, recoverable state (there's already something to open), not a
// real failure, so it's handled as its own case rather than shown as a
// generic error.
export const PUBLISHED_VERSION_EXISTS_ERROR_CODE = 7007;

// Shown when a version's PAIC agent run (selection_status) hasn't finished
// yet — the group/version is greyed out and blocked from selection until it
// completes.
export const AGENT_VERSION_NOT_COMPLETED_MESSAGE = "Agent Version is not completed";

// A run that reached selection_status "Failed" did finish — just
// unsuccessfully — so it gets its own message rather than reusing the
// "not completed" copy above, which would misleadingly suggest waiting it
// out will help.
export const AGENT_VERSION_FAILED_MESSAGE = "Agent Version run failed";

// sessionStorage key remembering which PAIC group's Select Version step the
// user was on right before an auto-redirect to the workspace (an already-
// generated version was picked). Navigating away swaps this whole route out
// for /org-design/workspace, so a plain browser Back remounts this wizard
// fresh at Select PAIC Group — this lets it jump straight back to Select
// Version for that same group instead.
export const PAIC_IMPORT_RETURN_GROUP_KEY = "orgDesignPaicImport.returnToProjectId";

// GET /versions (doc §8) rejects with this error_code instead of returning
// an empty array when the org/user has no published versions yet — an
// expected empty state, not a real failure, so it's treated as "no versions"
// rather than shown as a generic error.
export const ORG_REDESIGN_VERSION_NOT_FOUND_ERROR_CODE = 7004;

// ─── 5-state classification ──────────────────────────────────────────────
// Matches org_redesign_agent.py's finalize() verbatim — not reinvented here.
export const STATUS_LABELS = {
  UNCHANGED: "Unchanged",
  "AI AUGMENTED": "AI Augmented",
  REDUCED: "Reduced",
  REDUNDANT: "Redundant",
  CREATED: "Created",
};

export const STATUS_COLORS = {
  UNCHANGED: "default",
  "AI AUGMENTED": "processing",
  REDUCED: "warning",
  REDUNDANT: "error",
  CREATED: "success",
};

const STATUS_ACCENT_TOKEN = {
  UNCHANGED: "tertiary",
  "AI AUGMENTED": "info",
  REDUCED: "warningBorder",
  REDUNDANT: "danger",
  CREATED: "success",
};

export const ENTRY_OPTION = {
  EXISTING_ORG: "existing_org",
  PAIC_RECOMMENDED: "paic_recommended",
};

// "Augmented" is an AI-recommendation concept (a PAIC agent boosting a
// role's capacity/productivity, possibly with no headcount change at all —
// see [[project_org_design_augmented_status_semantics]]). A hand-built
// "existing org" workspace has no AI behind an AUGMENTED status — a role
// only ever lands there because someone manually raised its target
// headcount — so the same status is relabeled "Increased" for that entry
// path. This is display-only: the underlying status value stays AUGMENTED
// everywhere (stats, filters, colors, the save payload) so nothing about
// how it's computed or persisted changes, only how it reads.
const augmentedDisplayLabel = (entryOption) =>
  entryOption === ENTRY_OPTION.EXISTING_ORG ? "Increased" : STATUS_LABELS["AI AUGMENTED"];

// Other spellings the backend returns for the same verdict — saved versions
// come back as "AUGMENTED" (PAIC agent) or "INCREASED" (Current Org) rather
// than "AI AUGMENTED". A pure rename of the backend's own status, never a
// re-derivation from headcount, so an unrecognised spelling can't fall
// through to an uncolored raw-text tag or drop out of the status counts.
const STATUS_ALIASES = {
  AUGMENTED: "AI AUGMENTED",
  INCREASED: "AI AUGMENTED",
};

export const toCanonicalStatus = (statusRaw) => {
  const status = String(statusRaw || "UNCHANGED").trim().toUpperCase();
  return STATUS_ALIASES[status] || status;
};

// Single source of truth for how a role's status renders anywhere in the Org
// Design UI (tree node accent + tag, table Status cell, legend) — one lookup
// instead of scattering status-keyed if/else across components. `entryOption`
// is optional — omit it (as PAIC contexts do) to always get the AI-facing
// "Augmented" label.
export const getRoleChangeConfig = (statusRaw, entryOption) => {
  const status = toCanonicalStatus(statusRaw);
  const label = status === "AI AUGMENTED" ? augmentedDisplayLabel(entryOption) : STATUS_LABELS[status] || statusRaw;
  return {
    status,
    label,
    color: STATUS_COLORS[status] || "default",
    accentToken: STATUS_ACCENT_TOKEN[status] || STATUS_ACCENT_TOKEN.UNCHANGED,
  };
};

// Sourced from the budget sync response (baseline/added/augmented/reduced/
// redundant_role_count), not the client-computed statusCounts — the
// backend's counts are the source of truth here. A field the response
// doesn't return stays undefined and is filtered out rather than shown as a
// guessed 0. Baseline has no status/color of its own — it's a starting
// count, not a change. Shared by OrgDesignSummaryBridge and the PDF
// export's Summary page.
export const getRoleCountCards = (roleCounts, entryOption) =>
  [
    { key: "baseline", label: "Baseline", count: roleCounts?.baseline },
    { key: "added", label: getRoleChangeConfig("CREATED").label, count: roleCounts?.added, status: "CREATED" },
    { key: "augmented", label: getRoleChangeConfig("AI AUGMENTED", entryOption).label, count: roleCounts?.augmented, status: "AI AUGMENTED" },
    { key: "reduced", label: getRoleChangeConfig("REDUCED").label, count: roleCounts?.reduced, status: "REDUCED" },
    { key: "redundant", label: getRoleChangeConfig("REDUNDANT").label, count: roleCounts?.redundant, status: "REDUNDANT" },
  ].filter((card) => card.count != null);

export const TEXT = {
  PAGE_TITLE: "Organisation Design",
  PAGE_DESCRIPTION:
    "Turn a PAIC run's AI-agent recommendations — or your current organization — into a clear before/after design with headcount changes.",
  OPTION_A_TITLE: "Create workspace within existing org",
  OPTION_A_DESC: "Start from your current org hierarchy and design the target state by hand, with AI Evaluation available as a second opinion.",
  OPTION_B_TITLE: "Start with a PAIC recommended org",
  OPTION_B_DESC: "Import a completed PAIC run's AI-agent impact and generate an AI-recommended before/after org design to edit from.",
  PREVIOUS_ITERATIONS: "Previous Iterations",
  ORG_CONTEXT_TAB: "Organization Additional Strategic Context",
  BACK_TO_ORG_DESIGN: "Back to Organisation Design",
  BTN_SAVE_VERSION: "Save Version",
  BTN_DOWNLOAD_PDF: "Download PDF",
  VIEW_ORG: "Org View",
  VIEW_TABLE: "Table View",
};

export const BASELINE_TAG = {
  existing_org: "Current-org baseline",
  paic_recommended: "PAIC-baseline",
};

// Shared by OrgDesignWorkspace.jsx and PaicRecommendedImport.jsx — antd
// column `filters` options built from whatever distinct values actually
// appear in the current dataset, rather than a hardcoded list. `accessor`
// is optional — defaults to reading `key` straight off the row, pass one
// when the filtered value is derived (e.g. an array's length).
export const getFilters = (rows, key, accessor = (r) => r[key]) =>
  [...new Set(rows.map(accessor).filter((v) => v !== undefined && v !== null && v !== ""))]
    .sort((a, b) => (typeof a === "number" ? a - b : String(a).localeCompare(String(b))))
    .map((value) => ({ text: String(value), value }));

// Spread onto a clickable div/table row so it's also operable via keyboard —
// Enter/Space activates it, matching native button semantics.
export const activatableRowProps = (onActivate) => ({
  role: "button",
  tabIndex: 0,
  onKeyDown: (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onActivate();
    }
  },
});

// The `from` label every Org Design workspace analytics event carries. Lives
// here rather than in OrgDesignWorkspace.jsx so OrgTreeCard can share it
// without importing its own parent page back (page -> component -> page).
export const ORG_DESIGN_WORKSPACE_FROM = "org_design_workspace";
